"use client";

import {Fragment, JSX, ReactNode, useCallback, useEffect, useState} from "react";

import {useRouter, useSearchParams} from "next/navigation";

import {TripHelpButton} from "@/sections/trip/help-panel";
import {TripDetailLayout} from "@/sections/trip/layout";
import {TripLogoutButton} from "@/sections/trip/logout-button";
import {TripMobileHeader, TripMobileMenu, TripOverviewQuickLinks} from "@/sections/trip/mobile-nav";
import {TripSidebar} from "@/sections/trip/sidebar";

import {getTripSessionReservationId} from "@/entities/trip";
import type {TripUnitPmsPropertyManagement} from "@/entities/trip/api/types";

import {useMatchMedia} from "@/shared/hooks";
import {useTranslation} from "@/shared/i18n";
import type {TranslationKey} from "@/shared/i18n/types";
import {routes, TRIP_TABS, type TripTab} from "@/shared/utils/routing";

import "./styles/trip-detail-tabs.scss";

export interface TripDetailTabsProps {
    unit: {
        locationLabel: string;
        unitName: string;
    };
    stay: {
        checkInDate: string;
        checkOutDate: string;
        checkInWeekday: string;
        checkOutWeekday: string;
        checkInTime: string;
        checkOutTime: string;
    };
    support: TripUnitPmsPropertyManagement;
    panels: Record<TripTab, ReactNode>;
}

const MOBILE_TITLE_KEYS: Partial<Record<TripTab, TranslationKey>> = {
    [TRIP_TABS.pricing]: "trip.navPricing",
    [TRIP_TABS.gettingThere]: "trip.navGettingThere",
    [TRIP_TABS.amenities]: "trip.navAmenities",
    [TRIP_TABS.terms]: "trip.navRentalTerms",
    [TRIP_TABS.cancel]: "trip.navCancel",
};

function isTripTab(value: string | null): value is TripTab {
    return Object.values(TRIP_TABS).includes(value as TripTab);
}

export function TripDetailTabs({unit, stay, support, panels}: TripDetailTabsProps): JSX.Element | null {
    const router = useRouter();
    const searchParams = useSearchParams();
    const tabParam = searchParams.get("tab");
    const {t} = useTranslation();
    const isMobile = useMatchMedia({maxWidth: 767});

    const [activeTab, setActiveTab] = useState<TripTab>(isTripTab(tabParam) ? tabParam : TRIP_TABS.overview);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isCheckingSession, setIsCheckingSession] = useState(true);

    useEffect(() => {
        if (getTripSessionReservationId()) {
            setIsCheckingSession(false);
        } else {
            router.replace(routes.trip.list.url());
        }
    }, [router]);

    const handleSelectTab = useCallback((tab: TripTab): void => {
        setActiveTab(tab);
        const params = new URLSearchParams(window.location.search);
        params.set("tab", tab);
        window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
    }, []);

    if (isCheckingSession) {
        return null;
    }

    if (isMobile) {
        const isOverview = activeTab === TRIP_TABS.overview;
        const titleKey = MOBILE_TITLE_KEYS[activeTab];

        return (
            <div className="trip-mobile-shell">
                <TripMobileHeader
                    support={support}
                    title={titleKey ? t(titleKey) : undefined}
                    onBack={isOverview ? undefined : () => handleSelectTab(TRIP_TABS.overview)}
                    onOpenMenu={isOverview ? () => setIsMenuOpen(true) : undefined}
                />
                <div className="trip-mobile-shell__content">
                    <Fragment key={activeTab}>{panels[activeTab]}</Fragment>
                    {isOverview && <TripOverviewQuickLinks onSelectTab={handleSelectTab} />}
                </div>
                <TripMobileMenu
                    open={isMenuOpen}
                    onClose={() => setIsMenuOpen(false)}
                    support={support}
                    unitName={unit.unitName}
                    checkInDate={stay.checkInDate}
                    checkOutDate={stay.checkOutDate}
                    onSelectTab={(tab) => {
                        handleSelectTab(tab);
                        setIsMenuOpen(false);
                    }}
                />
            </div>
        );
    }

    return (
        <TripDetailLayout
            headerAction={
                <>
                    <TripHelpButton support={support} />
                    <TripLogoutButton />
                </>
            }
            sidebar={<TripSidebar unit={unit} stay={stay} activeTab={activeTab} onSelectTab={handleSelectTab} />}
        >
            <Fragment key={activeTab}>{panels[activeTab]}</Fragment>
            {activeTab === TRIP_TABS.overview && <TripOverviewQuickLinks onSelectTab={handleSelectTab} tabs={[TRIP_TABS.pricing, TRIP_TABS.terms]} />}
        </TripDetailLayout>
    );
}
